import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { getCookie } from 'commercial-web-client-utilities/dist/cookie';

import { useNonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';
import { useLedsService } from 'commercial-web-client-utilities/dist/composables/leds-service';
import { deviceInfoService } from '@/composables/device-info-service';
import { createMemberLoginService } from '@/composables/member-login-service';

import { AuthenticateErrorCode } from '@/enums/member-login-responses';
import { regionCodes, releaseTags, legacyCommwebServiceBaseUrls } from '@/constants/auth-constants';

import type { AxiosError } from 'axios';
import type { MemberLoginResponse, LedsErrorResponse } from '@/types';

interface UseLoginFormOptions {
    aemSiteIdentifier: string;
    usernamePrefilled?: boolean;
}

type LoginFormErrorType = 'validation' | 'system' | 'locked' | 'usernameUnknown'
    | 'attempt1' | 'attempt2' | 'rsaLocked' | null;

interface LoginFormAlert {
    i18nKey: string;
    variant: 'danger' | 'warning';
    numberOfTries: number;
    testRef: string;
}

export function useLoginForm(options: UseLoginFormOptions) {
    const { t } = useI18n();
    const nonSecureService = useNonsecureService();
    const ledsService = useLedsService();
    const memberLoginService = createMemberLoginService({
        nonSecureService: nonSecureService,
        ledsService: ledsService
    });

    /*-----------*\
    | Form Inputs |
    \*-----------*/

    const selectedSwimlane = ref<string>(getCookie('cwVueClientReleaseTag') || '');
    const selectedRegion = ref<string>('');
    const username = ref<string>('');
    const password = ref<string>('');
    const passwordInputType = ref<'text' | 'password'>('password');
    const passwordVisibilityIcon = ref<string>('fa-regular fa-eye');

    const usernameHasError = computed(() => {
        return (v$.value.username.$error && v$.value.username.$dirty) || errorType.value !== null;
    });

    const passwordHasError = computed(() => {
        return (v$.value.password.$error && v$.value.password.$dirty) || errorType.value !== null;
    });

    const passwordIsRequired = computed(() => {
        return (v$.value.password.$error && v$.value.password.$dirty);
    });

    const regionOptions = computed(() => {
        const options: Array<{ value: string | null, text: string }> = [
            { value: '', text: t('login.label.default') }
        ];
        return options.concat(
            regionCodes.map(code => ({
                value: code,
                text: code
            }))
        );
    });

    const showRegionSelection = computed(() => {
        const environment = window.location.hostname.replace(/^secure\./, '').split('.')[0];
        return /(localhost|unit|system)/.test(environment);
    });

    const swimlaneOptions = computed(() => {
        return [
            { value: '', text: t('login.label.default') },
            ...releaseTags.map(code => ({ value: code, text: code }))
        ];
    });

    const showSwimlaneSelection = computed(() => {
        const environment = window.location.hostname.replace(/^secure\./, '').split('.')[0];
        return /(localhost|unit|system|qual|qa|isc|mirror|preview|demo)/.test(environment);
    });

    function togglePasswordVisibility() {
        passwordInputType.value = passwordInputType.value == 'text' ? 'password' : 'text';
        passwordVisibilityIcon.value = passwordInputType.value == 'text'
            ? 'fa-regular fa-eye-slash'
            : 'fa-regular fa-eye';
    }

    /*--------------*\
    | Error Handling |
    \*--------------*/

    const loading = ref<boolean>(false);
    const errorType = ref<LoginFormErrorType>(null);

    const form = reactive({
        get username() {
            return username.value;
        },
        set username(newUsername: string) {
            username.value = newUsername;
        },
        get password() {
            return password.value;
        },
        set password(newPassword) {
            password.value = newPassword;
        },
    });

    const loginRules = {
        username: {
            required,
        },
        password: {
            required,
        },
    };

    const v$ = useVuelidate(loginRules, form);

    function validatePassword() {
        v$.value.password.$touch();
    }

    /*--------------*\
    | Alert Messages |
    \*--------------*/

    const submissionAlert = computed<LoginFormAlert | null>(() => {
        switch (errorType.value) {
            case 'attempt1':
                if (options.usernamePrefilled) {
                    return {
                        i18nKey: 'forgotUsername.loginForm.invalidMessage',
                        variant: 'warning',
                        numberOfTries: 2,
                        testRef: 'member-login-alert-attempt-1',
                    };
                }
                return {
                    i18nKey: 'login.messages.firstAttempt',
                    variant: 'warning',
                    numberOfTries: 2,
                    testRef: 'member-login-alert-attempt-1',
                };
            case 'attempt2':
                if (options.usernamePrefilled) {
                    return {
                        i18nKey: 'forgotUsername.loginForm.invalidMessage',
                        variant: 'warning',
                        numberOfTries: 1,
                        testRef: 'member-login-alert-attempt-2',
                    };
                }
                return {
                    i18nKey: 'login.messages.secondAttempt',
                    variant: 'warning',
                    numberOfTries: 1,
                    testRef: 'member-login-alert-attempt-2',
                };
            case 'locked':
                return {
                    i18nKey: 'login.messages.accountLocked',
                    variant: 'warning',
                    numberOfTries: 0,
                    testRef: 'member-login-alert-locked',
                };
            case 'rsaLocked':
                return {
                    i18nKey: 'login.messages.mfaLocked',
                    variant: 'warning',
                    numberOfTries: 0,
                    testRef: 'member-login-mfa-alert-locked',
                };
            case 'usernameUnknown':
                return {
                    i18nKey: 'login.messages.usernameUnknownError',
                    variant: 'warning',
                    numberOfTries: 3,
                    testRef: 'member-login-invalid-username-unknown',
                };
            case 'validation':
                if (options.usernamePrefilled) {
                    return {
                        i18nKey: 'forgotUsername.loginForm.validations.requiredPassword',
                        variant: 'warning',
                        numberOfTries: 3,
                        testRef: 'member-login-alert-validation',
                    };
                }
                return {
                    i18nKey: 'login.messages.validationError',
                    variant: 'warning',
                    numberOfTries: 3,
                    testRef: 'member-login-alert-validation',
                };
            case 'system':
                return {
                    i18nKey: 'login.messages.systemError',
                    variant: 'danger',
                    numberOfTries: 0,
                    testRef: 'member-login-system-error-alert',
                };
            default:
                return null;
        }
    });

    /*---------------*\
    | Form Submission |
    \*---------------*/

    // TODO - What are we going to replace this with? How do we identify openshift namespace instead?
    const legacyPortalSwimlaneUrl = computed(() => {
        if (!showSwimlaneSelection.value) {
            return legacyCommwebServiceBaseUrls.latest.replace(/\/$/, '');
        } else if (selectedSwimlane.value && legacyCommwebServiceBaseUrls[selectedSwimlane.value]) {
            return legacyCommwebServiceBaseUrls[selectedSwimlane.value].replace(/\/$/, '');
        } else {
            return window.location.origin.replace('https://secure.', 'https://');
        }
    });

    const legacyPortalEnvironment = computed(() => {
        if (!showSwimlaneSelection.value || !selectedSwimlane.value || selectedSwimlane.value === 'latest') {
            return '';
        } else {
            return legacyPortalSwimlaneUrl.value.replace('https://', '') + '/';
        }
    });

    const secureRedirectBaseUrl = computed(() => {
        return legacyPortalSwimlaneUrl.value.replace('https://', 'https://secure.');
    });

    async function login() {
        if (loading.value) return;

        try {
            const isValid = await v$.value.$validate();
            if (!isValid) {
                errorType.value = 'validation';
                v$.value.$touch();
                return;
            }

            loading.value = true;

            const deviceInfo = await deviceInfoService.getDeviceInfo();
            const payload = {
                username: form.username.trim(),
                password: form.password,
                deviceInfo,
            };

            const region = showRegionSelection.value ? selectedRegion.value : null;
            const response = await memberLoginService.memberLogin({
                payload,
                signal: new AbortController().signal,
                releaseTag: selectedSwimlane.value,
                legacyPortalEnvironment: legacyPortalEnvironment.value,
                region
            });

            const { status, data } = response;

            const redirectQuery = new URLSearchParams();
            if (status === 200) {
                const { authToken } = data as
                    Required<Pick<MemberLoginResponse, 'authToken'>>;
                redirectQuery.append('authToken', authToken);
            } else if (status === 428) {
                const { updatePasswordToken } = data as
                    Required<Pick<MemberLoginResponse, 'updatePasswordToken'>>;
                redirectQuery.append('updatePasswordToken', updatePasswordToken);
            } else if (status === 434) {
                const { mfaToken } = data as
                    Required<Pick<MemberLoginResponse, 'mfaToken'>>;
                redirectQuery.append('mfaToken', mfaToken);
            } else {
                throw new Error(`Unexpected response status: ${status}`);
            }

            if (options.aemSiteIdentifier) {
                redirectQuery.append('site', options.aemSiteIdentifier);
            }

            if (region) {
                redirectQuery.append('region', region);
            }
            redirectQuery.append('swimlane', legacyPortalSwimlaneUrl.value);

            window.location.href = `${secureRedirectBaseUrl.value}/site-router.html?${redirectQuery.toString()}`;

            errorType.value = null;
            v$.value.$reset();

        } catch (axiosError) {
            const error = axiosError as AxiosError<LedsErrorResponse>;
            const status = error?.response?.status ?? 500;
            const errorCodes = error?.response?.data?.errorCodes || [];
            const errorData = error?.response?.data?.errorData || {};

            const invalidLoginAttemptsCount = errorData.invalidLoginAttemptsCount || 0;

            if (status === 401) {
                if (errorCodes.includes(AuthenticateErrorCode.ACCOUNT_LOCKED)) {
                    errorType.value = 'locked';
                } else if (
                    errorCodes.includes(AuthenticateErrorCode.USERNAME_OR_PASSWORD_INVALID)
                ) {
                    if (invalidLoginAttemptsCount === 0) {
                        errorType.value = 'usernameUnknown';
                    } else if (invalidLoginAttemptsCount === 1) {
                        errorType.value = 'attempt1';
                    } else if (invalidLoginAttemptsCount === 2) {
                        errorType.value = 'attempt2';
                    } else {
                        errorType.value = 'locked';
                    }
                }
            } else if (status === 436) {
                errorType.value = 'rsaLocked';
            } else {
                errorType.value = 'system';
            }
        } finally {
            loading.value = false;
        }
    }

    /*-----*\
    | Links |
    \*-----*/

    const aemBaseUrl = computed<string>(() => {
        if (window.location.origin.includes('secure.')) {
            return window.location.origin + '/';
        }
        const swimlane = selectedSwimlane.value ?? releaseTags[0];
        const baseUrl = legacyCommwebServiceBaseUrls[swimlane] ?? '';
        return baseUrl.replace('https://', 'https://secure.');
    });

    const forgotUsernameLink = computed<string>(() => {
        // eslint-disable-next-line max-len
        return `${aemBaseUrl.value}site-router.html?site=${options.aemSiteIdentifier}&target=forgot-username&region=${selectedRegion.value}`;
    });

    const forgotPasswordLink = computed<string>(() => {
        // eslint-disable-next-line max-len
        return `${aemBaseUrl.value}site-router.html?site=${options.aemSiteIdentifier}&target=forgot-password&region=${selectedRegion.value}`;
    });

    const registerLink = computed<string>(() => {
        // eslint-disable-next-line max-len
        return `${aemBaseUrl.value}site-router.html?site=${options.aemSiteIdentifier}&target=registration&region=${selectedRegion.value}`;
    });

    /*----------------*\
    | Public Interface |
    \*----------------*/

    return {
        selectedSwimlane,
        swimlaneOptions,
        showSwimlaneSelection,

        selectedRegion,
        regionOptions,
        showRegionSelection,

        username,
        usernameHasError,

        password,
        passwordHasError,
        passwordIsRequired,
        passwordInputType,
        passwordVisibilityIcon,
        togglePasswordVisibility,
        validatePassword,

        loading,

        submissionAlert,

        login,

        forgotUsernameLink,
        forgotPasswordLink,
        registerLink,
    };
}
